/**
 * driver.js configuration tuned to our design tokens.
 *
 * The library is BYO-CSS — we ship its base stylesheet plus a few
 * overrides on the popover so it inherits ``--brand-primary`` /
 * ``--gray-*`` instead of the default driver palette.
 *
 * Reduced-motion: driver.js animates via inline transitions, not CSS
 * keyframes, so we toggle ``animate: false`` based on the
 * ``prefers-reduced-motion`` media query at construction time
 * (CROSS_PAGE_DECISIONS §5).
 */
import { driver, type Config, type Driver } from "driver.js";
import "driver.js/dist/driver.css";

const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

export interface TourStop {
  /**
   * CSS selector for the element to spotlight. Matches must be present
   * in the DOM at the time the stop fires; missing selectors cause
   * driver.js to skip with a console warning. ``null`` = modal-style
   * stop (centered, no spotlight).
   */
  selector: string | null;
  title: string;
  description: string;
  /**
   * Optional pre-step navigation. The tour runner pushes this route
   * before showing the stop (giving React a tick to render the target
   * before the spotlight engages).
   */
  route?: string;
  /** Optional anchor side hint — driver.js auto-selects otherwise. */
  side?: "top" | "right" | "bottom" | "left";
}

export const driverDefaults: Config = {
  animate: !prefersReducedMotion(),
  showProgress: true,
  showButtons: ["next", "previous", "close"],
  nextBtnText: "Next ▸",
  prevBtnText: "◂ Back",
  doneBtnText: "Finish",
  popoverClass: "rebot-tour",
  // Higher than every overlay we ship (modals = 50, dropdowns = 40);
  // keeps the spotlight above brand-coloured site detail headers.
  stagePadding: 6,
  stageRadius: 8,
};

/**
 * Builds a driver.js instance pre-configured with our defaults plus
 * any caller overrides. Exposed as a factory so tests can stub it +
 * the GettingStartedPage can construct one per launch.
 */
export function makeDriver(overrides: Partial<Config> = {}): Driver {
  return driver({ ...driverDefaults, ...overrides });
}
